<script setup>
import CustomerSelect from '@/Components/CustomerSelect.vue';
import { Head, useForm, usePage } from '@inertiajs/vue3';
import DialogModal from '@/Components/DialogModal.vue';
import FullWidthDialogModal from '@/Components/FullWidthDialogModal.vue';
import FormSection from '@/Components/FormSection.vue';
import SelectField from '@/Components/SelectField.vue';
import SignatureField from '@/Components/SignatureField.vue';
import TextField from '@/Components/TextField.vue';
import { computed, ref } from 'vue';
import TextareaField from '@/Pages/Order/TextareaField.vue';
import { mdiPlus } from '@mdi/js';
import OutlinedSheet from '@/Components/OutlinedSheet.vue';

const page = usePage();
const accounts = ref([...page.props.accounts]);
const draftSwapId = ref(null);
const showConfirmationModal = ref(false);
const showCreateAccountModal = ref(false);
const createAccountProcessing = ref(false);
const createAccountName = ref('');
const createAccountErrors = ref({});
const reviewProcessing = ref(false);
const signProcessing = ref(false);

const nowDateTimeLocal = () => {
    const now = new Date();
    const timezoneOffset = now.getTimezoneOffset() * 60000;

    return new Date(now.getTime() - timezoneOffset).toISOString().slice(0, 16);
};

const form = useForm({
    pallet_swap_id: null,
    account_id: null,
    customer_number: null,
    swap_date: nowDateTimeLocal(),
    delivered_pallets: 0,
    received_pallets: 0,
    driver_name: '',
    number_plate: '',
    note: '',
    signature: '',
});

const resetForm = () => {
    draftSwapId.value = null;
    form.reset(
        'pallet_swap_id',
        'account_id',
        'customer_number',
        'delivered_pallets',
        'received_pallets',
        'number_plate',
        'driver_name',
        'note',
        'signature',
    );
    form.swap_date = nowDateTimeLocal();
    form.driver_name = '';
    form.number_plate = '';
    form.delivered_pallets = 0;
    form.received_pallets = 0;
};

const reviewAndSign = async () => {
    reviewProcessing.value = true;
    form.clearErrors();

    try {
        const response = await axios.post(route('pallet-swap.review'), {
            pallet_swap_id: draftSwapId.value,
            account_id: form.account_id,
            customer_number: form.customer_number,
            swap_date: form.swap_date,
            delivered_pallets: form.delivered_pallets,
            received_pallets: form.received_pallets,
            driver_name: form.driver_name,
            number_plate: form.number_plate,
            note: form.note,
        });

        draftSwapId.value = response.data.id;
        form.pallet_swap_id = response.data.id;
        form.signature = '';
        showConfirmationModal.value = true;
    } catch (error) {
        if (error.response?.status === 422) {
            form.setError(error.response.data.errors);
        } else {
            throw error;
        }
    } finally {
        reviewProcessing.value = false;
    }
};

const submitSignature = async () => {
    signProcessing.value = true;
    form.clearErrors('signature');

    try {
        await axios.put(
            route('pallet-swap.sign', {
                pallet_swap: draftSwapId.value,
            }),
            {
                signature: form.signature,
            },
        );

        showConfirmationModal.value = false;
        resetForm();
    } catch (error) {
        if (error.response?.status === 422) {
            form.setError(error.response.data.errors);
        } else {
            throw error;
        }
    } finally {
        signProcessing.value = false;
    }
};

const accountItems = computed(() => {
    return accounts.value;
});

const selectedAccount = computed(() => {
    return (
        accounts.value.find((account) => account.id === form.account_id) ?? null
    );
});

const closeConfirmationModal = () => {
    showConfirmationModal.value = false;
};

const openCreateAccountModal = () => {
    createAccountName.value = '';
    createAccountErrors.value = {};
    showCreateAccountModal.value = true;
};

const closeCreateAccountModal = () => {
    showCreateAccountModal.value = false;
};

const createAccount = async () => {
    createAccountProcessing.value = true;
    createAccountErrors.value = {};

    try {
        const response = await axios.post(route('pallet-swap.account.store'), {
            name: createAccountName.value,
        });

        accounts.value = [...accounts.value, response.data].sort(
            (left, right) => left.name.localeCompare(right.name),
        );
        form.account_id = response.data.id;
        closeCreateAccountModal();
    } catch (error) {
        if (error.response?.status === 422) {
            createAccountErrors.value = error.response.data.errors;
        } else {
            throw error;
        }
    } finally {
        createAccountProcessing.value = false;
    }
};
</script>

<template>
    <v-container>
        <Head :title="$t('Pallet Swap')" />

        <FormSection full-width @submitted="reviewAndSign">
            <template #title>{{ $t('New Pallet Swap') }}</template>
            <template #description>
                {{
                    $t(
                        'Enter the pallet movement, sign it on the phone, and save it directly.',
                    )
                }}
            </template>
            <template #form>
                <div class="tw-flex">
                    <SelectField
                        v-model="form.account_id"
                        :items="accountItems"
                        item-title="name"
                        item-value="id"
                        :label="$t('Pallet Account')"
                        :error-messages="form.errors.account_id"
                    />
                    <v-btn
                        type="button"
                        class="tw-ml-2 tw-mt-2"
                        size="small"
                        color="primary"
                        variant="outlined"
                        :icon="mdiPlus"
                        @mousedown.prevent
                        @click="openCreateAccountModal"
                    />
                </div>
                <CustomerSelect
                    v-model="form.customer_number"
                    :placeholder="$t('Customer')"
                    clearable
                    :error-messages="form.errors.customer_number"
                />
                <TextField
                    v-model="form.swap_date"
                    type="datetime-local"
                    :label="$t('Swap Date and Time')"
                    :error-messages="form.errors.swap_date"
                />
                <v-row>
                    <v-col cols="12" md="6">
                        <TextField
                            v-model="form.delivered_pallets"
                            type="number"
                            :label="$t('Delivered Pallets')"
                            :error-messages="form.errors.delivered_pallets"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <TextField
                            v-model="form.received_pallets"
                            type="number"
                            :label="$t('Received Pallets')"
                            :error-messages="form.errors.received_pallets"
                        />
                    </v-col>
                </v-row>
                <TextareaField
                    v-model="form.note"
                    :label="$t('Note')"
                    :error-messages="form.errors.note"
                />

                <OutlinedSheet>
                    <template #title> {{ $t('Driver Info') }} </template>
                    <TextField
                        v-model="form.driver_name"
                        :label="$t('Driver Name')"
                        :error-messages="form.errors.driver_name"
                    />
                    <TextField
                        v-model="form.number_plate"
                        :label="$t('Number Plate')"
                        :error-messages="form.errors.number_plate"
                    />
                </OutlinedSheet>
            </template>
            <template #actions>
                <v-btn
                    type="submit"
                    color="primary"
                    variant="elevated"
                    :loading="reviewProcessing"
                >
                    {{ $t('Review and Sign') }}
                </v-btn>
            </template>
        </FormSection>

        <FullWidthDialogModal
            :show="showConfirmationModal"
            full-height
            @close="closeConfirmationModal"
        >
            <template #title>{{ $t('Confirm Pallet Swap') }}</template>
            <template #content>
                <v-sheet class="tw-mb-4 tw-rounded-lg tw-bg-slate-50 tw-p-4">
                    <div
                        class="tw-mb-3 tw-text-sm tw-font-medium tw-text-slate-500"
                    >
                        {{ $t('Summary') }}
                    </div>
                    <div class="tw-grid tw-gap-3 sm:tw-grid-cols-2">
                        <div>
                            <div
                                class="tw-text-xs tw-uppercase tw-text-slate-400"
                            >
                                {{ $t('Pallet Account') }}
                            </div>
                            <div
                                class="tw-text-sm tw-font-medium tw-text-slate-900"
                            >
                                {{ selectedAccount?.name ?? '-' }}
                            </div>
                        </div>
                        <div>
                            <div
                                class="tw-text-xs tw-uppercase tw-text-slate-400"
                            >
                                {{ $t('Customer Number') }}
                            </div>
                            <div
                                class="tw-text-sm tw-font-medium tw-text-slate-900"
                            >
                                {{ form.customer_number || '-' }}
                            </div>
                        </div>
                        <div>
                            <div
                                class="tw-text-xs tw-uppercase tw-text-slate-400"
                            >
                                {{ $t('Swap Date') }}
                            </div>
                            <div
                                class="tw-text-sm tw-font-medium tw-text-slate-900"
                            >
                                {{ form.swap_date || '-' }}
                            </div>
                        </div>
                        <div>
                            <div
                                class="tw-text-xs tw-uppercase tw-text-slate-400"
                            >
                                {{ $t('Driver Name') }}
                            </div>
                            <div
                                class="tw-text-sm tw-font-medium tw-text-slate-900"
                            >
                                {{ form.driver_name || '-' }}
                            </div>
                        </div>
                        <div>
                            <div
                                class="tw-text-xs tw-uppercase tw-text-slate-400"
                            >
                                {{ $t('Number Plate') }}
                            </div>
                            <div
                                class="tw-text-sm tw-font-medium tw-text-slate-900"
                            >
                                {{ form.number_plate || '-' }}
                            </div>
                        </div>
                        <div>
                            <div
                                class="tw-text-xs tw-uppercase tw-text-slate-400"
                            >
                                {{ $t('Delivered / Received') }}
                            </div>
                            <div
                                class="tw-text-sm tw-font-medium tw-text-slate-900"
                            >
                                {{ form.delivered_pallets }} /
                                {{ form.received_pallets }}
                            </div>
                        </div>
                        <div class="sm:tw-col-span-2">
                            <div
                                class="tw-text-xs tw-uppercase tw-text-slate-400"
                            >
                                {{ $t('Note') }}
                            </div>
                            <div
                                class="tw-text-sm tw-font-medium tw-text-slate-900"
                            >
                                {{ form.note || '-' }}
                            </div>
                        </div>
                    </div>
                </v-sheet>
                <SignatureField
                    v-model="form.signature"
                    :label="$t('Confirmation Signature')"
                    :error-messages="form.errors.signature"
                />
            </template>
            <template #footer>
                <v-btn
                    type="button"
                    variant="text"
                    @click="closeConfirmationModal"
                >
                    {{ $t('Cancel') }}
                </v-btn>
                <v-btn
                    type="button"
                    color="primary"
                    variant="elevated"
                    :loading="signProcessing"
                    @click="submitSignature"
                >
                    {{ $t('Confirm and Save') }}
                </v-btn>
            </template>
        </FullWidthDialogModal>

        <DialogModal
            :show="showCreateAccountModal"
            @close="closeCreateAccountModal"
        >
            <template #title>{{ $t('Create Pallet Account') }}</template>
            <template #content>
                <TextField
                    v-model.trim="createAccountName"
                    :label="$t('Account Name')"
                    :error-messages="createAccountErrors.name"
                />
            </template>
            <template #footer>
                <v-btn
                    type="button"
                    variant="text"
                    @click="closeCreateAccountModal"
                >
                    {{ $t('Cancel') }}
                </v-btn>
                <v-btn
                    type="button"
                    color="primary"
                    variant="elevated"
                    :loading="createAccountProcessing"
                    @click="createAccount"
                >
                    {{ $t('Create') }}
                </v-btn>
            </template>
        </DialogModal>
    </v-container>
</template>
